import { action, computed, makeObservable } from 'mobx'

import ScanStationFilterType, {
  getScanStationFilterTypeCaption,
  scanStationFilterTypes,
} from '~/client/src/desktop/enums/ScanStationFilterType'
import DesktopEventsStore from '~/client/src/desktop/stores/EventStore/DesktopEvents.store'
import { UNASSIGNED_FILTER_OPTION } from '~/client/src/shared/components/Deliveries/DeliveriesView.store'
import { LocalStorageKey } from '~/client/src/shared/enums/LocalStorageKey'
import IScannerHistoryPair from '~/client/src/shared/models/IScannerHistoryPair'
import UserProject from '~/client/src/shared/models/UserProject'
import CompaniesStore from '~/client/src/shared/stores/domain/Companies.store'
import LocationAttributesStore from '~/client/src/shared/stores/domain/LocationAttributes.store'
import ProjectMembersStore from '~/client/src/shared/stores/domain/ProjectMembers.store'
import ScannersStore from '~/client/src/shared/stores/domain/Scanners.store'
import TagsStore from '~/client/src/shared/stores/domain/Tags.store'
import UserProjectsStore from '~/client/src/shared/stores/domain/UserProjects.store'
import { RESET_ALL_FILTERS } from '~/client/src/shared/stores/EventStore/eventConstants'

import ReportsStore from '../../../../Reports.store'
import DeliveryReportListStore from '../../../ReportsList/DeliveryReport/DeliveryReportList.store'
import FormReportListStore from '../../../ReportsList/FormReportList/FormReportList.store'
import ScanStationListStore from '../../../ReportsList/ScanStation/ScanStationList.store'
import BaseReportsFilterStore, { ISourceMap } from './BaseReportsFilter.store'
import ReportsFilterStore from './ReportsFilter.store'

export default class ScanStationFilterStore extends ReportsFilterStore {
  public constructor(
    eventsStore: DesktopEventsStore,
    instancesStore: ReportsStore,
    instancesListStore:
      | ScanStationListStore
      | FormReportListStore
      | DeliveryReportListStore,
    onShowChanged: (isShown: boolean, filterType: string) => void,
    scannersStore: ScannersStore,
    companiesStore: CompaniesStore,
    projectMembersStore: ProjectMembersStore,
    userProjectsStore: UserProjectsStore,
    locationAttributesStore: LocationAttributesStore,
    tagsStore: TagsStore,
  ) {
    super(
      eventsStore,
      instancesStore,
      instancesListStore,
      onShowChanged,
      scannersStore,
      companiesStore,
      projectMembersStore,
      userProjectsStore,
      locationAttributesStore,
      tagsStore,
    )

    makeObservable(this)
  }

  protected get availableInstances(): IScannerHistoryPair[] {
    return this.instancesStore.allScanStationHistory
  }

  @computed
  public get filterStoresByTypeMap(): {
    [filterType: string]: BaseReportsFilterStore
  } {
    const map: { [filterType: string]: BaseReportsFilterStore } = {}

    scanStationFilterTypes.forEach(filterType => {
      const { appState } = this.eventsStore

      map[filterType] = new BaseReportsFilterStore(
        filterType,
        appState,
        this.sourceMapByFilterTypeMap[filterType],
        this.instancesListStore,
        this.onShowChanged,
        appState.scanStationFilters.fieldsMap,
        getScanStationFilterTypeCaption,
        this.getOptionName,
        LocalStorageKey.ScanStationReportFilter,
      )
    })
    return map
  }

  @action.bound
  public resetAllFilters() {
    scanStationFilterTypes.forEach(type =>
      localStorage.removeItem(
        `${this.eventsStore.appState.activeProject.id}_${LocalStorageKey.ScanStationReportFilter}_${type}`,
      ),
    )
    this.eventsStore.dispatch(RESET_ALL_FILTERS)
  }

  @computed
  protected get sourceMapByFilterTypeMap(): {
    [filterType: string]: ISourceMap
  } {
    const maps = scanStationFilterTypes.reduce((acc, filterType) => {
      acc[filterType] = this.getDefaultSourceMapByType(filterType)
      return acc
    }, {})

    this.availableInstances.forEach(instance => {
      Object.keys(maps).forEach(filterType => {
        const map = maps[filterType]
        let optionIds: string[] = []

        switch (filterType) {
          case ScanStationFilterType.COMPANY:
            const user = this.projectMembersStore.getById(
              instance.history.userId,
            )
            if (user) {
              const companyId = UserProject.getCompanyId(
                user,
                this.userProjectsStore,
              )
              optionIds = [companyId || UNASSIGNED_FILTER_OPTION]
            } else {
              optionIds = [UNASSIGNED_FILTER_OPTION]
            }
            break
          case ScanStationFilterType.SCANNER:
            optionIds = [instance.history.scannerId || UNASSIGNED_FILTER_OPTION]
            break
        }

        optionIds.forEach(optionId => {
          if (!map[optionId]) {
            optionId = UNASSIGNED_FILTER_OPTION
          }

          map[optionId].push(instance.id)
        })
      })
    })
    return maps
  }

  protected getDefaultSourceMapByType(type: ScanStationFilterType) {
    let sourceList: string[] = []

    switch (type) {
      case ScanStationFilterType.SCANNER:
        sourceList = [
          ...this.scannersStore.scannersList.map(scanner => scanner.id),
          UNASSIGNED_FILTER_OPTION,
        ]
        break
      case ScanStationFilterType.COMPANY:
        sourceList = [
          ...this.companiesStore.availableSortedCompanies.map(c => c.id),
          UNASSIGNED_FILTER_OPTION,
        ]
        break
    }

    return sourceList.reduce((acc, optionId) => {
      acc[optionId] = []
      return acc
    }, {})
  }

  protected fieldsMap() {
    return this.eventsStore.appState.scanStationFilters.fieldsMap
  }
}
